import Zoomist from 'zoomist'
import { ZoomistOptions } from 'zoomist/types'

/**
 * Class responsible for managing zoom functionality in a product carousel.
 */
export class Zoomer {
    private zoomInstance: Zoomist
    /**
     * Creates an instance of Zoomer.
     * @param trigger - The element that triggers zooming.
     * @param target - The initial target element for zooming.
     * @param options - The zoomist instance options
     * @param zoomistContainer - the zoomist container
     */
    constructor(
        private readonly trigger: HTMLElement,
        private target: HTMLImageElement | null,
        private readonly options: ZoomistOptions,
        private readonly zoomistContainer: HTMLElement,
    ) {
        this.setupTouchZoom()
        this.setupClickZoom()
        this.zoomInstance = new Zoomist(this.zoomistContainer, this.options)
    }

    /**
     * Initializes Zoomist with the current target.
     */
    private updateZoomistImage(): void {
        if (! this.target || ! this.zoomistContainer) {
            return
        }

        const imgElement = this.zoomistContainer.querySelector('img')
        if (imgElement) {
            imgElement.src = this.target.src
        }
    }

    /**
     * Sets up touch-based zoom functionality.
     */
    public setupTouchZoom(): void {
        const DOUBLE_TAP_THRESHOLD = 500 // milliseconds
        let lastTapTime = 0
        let tapTimeout: number | null = null

        const resetTapTimeout = () => {
            if (tapTimeout !== null) {
                clearTimeout(tapTimeout)
                tapTimeout = null
            }
        }

        const handleSingleTap = (currentTime: number) => {
            resetTapTimeout()
            tapTimeout = setTimeout(() => {
                lastTapTime = 0
                tapTimeout = null
            }, DOUBLE_TAP_THRESHOLD) as unknown as number
            lastTapTime = currentTime
        }

        const handleTouchGesture = () => {
            this.updateZoomerVisibility(true)
            this.updateZoomistImage()
            resetTapTimeout()
            lastTapTime = 0
        }

        const isZoomedIn = () => this.trigger.classList.contains('show-zoom-out')

        if (this.target) {
            this.target.addEventListener('touchstart', (event: TouchEvent) => {
                const currentTime = new Date().getTime()
                const tapLength = currentTime - lastTapTime

                // Pinch-to-zoom (two fingers)
                if (event.touches.length === 2 && ! isZoomedIn()) {
                    handleTouchGesture()
                    if (event.cancelable) event.preventDefault()
                    return
                }

                // Single tap (potential start of a double-tap)
                if (event.touches.length === 1 && ! isZoomedIn()) {
                    if (tapLength < DOUBLE_TAP_THRESHOLD) {
                        handleTouchGesture()
                        if (event.cancelable) event.preventDefault()
                    } else {
                        handleSingleTap(currentTime)
                    }
                }
            })
        }
    }

    /**
     * Sets up click-based zoom functionality.
     */
    private setupClickZoom(): void {
        if (this.trigger) {
            this.trigger.addEventListener('click', (event) => {
                const zoomedIn = this.trigger.classList.contains('show-zoom-out')
                this.updateZoomerVisibility(! zoomedIn)
                this.zoomInstance.reset()

                if ( ! zoomedIn) {
                    this.updateZoomistImage()
                }
                if (event.cancelable) event.preventDefault()
            })
        }
    }

    public updateZoomerVisibility(show: boolean): void {
        this.zoomistContainer.classList.toggle('show', show)

        if (this.trigger) {
            this.trigger.classList.toggle('show-zoom-out', show)
            this.trigger.setAttribute('aria-label', show ? 'Close Zoom' : 'Open Zoom')
        }
    }

    /**
     * Sets a new target for the zoom functionality.
     * @param target - The new target element selector.
     */
    public setTarget(target: HTMLImageElement | null): void {
        this.target = target
    }
}
